import React from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiClock,
  FiCompass,
  FiMapPin,
  FiShield,
  FiStar,
  FiUsers,
} from "react-icons/fi";
import beach from "../assets/beach.jpg";
import gorilla from "../assets/gorilla.jpg";
import lions from "../assets/lions.jpg";
import leopard from "../assets/Leopaed.jpg";

const holidayCards = [
  {
    title: "Gorilla Trekking",
    location: "Bwindi & Volcanoes",
    duration: "3-5 days",
    image: gorilla,
    text: "Secure permits, expert ranger guidance, forest lodges, and smooth transfers.",
  },
  {
    title: "Big Game Safaris",
    location: "Uganda, Kenya & Tanzania",
    duration: "5-10 days",
    image: lions,
    text: "Private game drives through savannah parks with flexible daily pacing.",
  },
  {
    title: "Primate & Beach Escapes",
    location: "Uganda to Zanzibar",
    duration: "8-12 days",
    image: beach,
    text: "Track primates, cruise wild rivers, then unwind on the Indian Ocean coast.",
  },
];

const tripBenefits = [
  "Tailor-made safari planning",
  "Local guides and park expertise",
  "Permits, lodges, and transfers arranged",
  "Flexible luxury, midrange, and family options",
];

const stats = [
  { value: "12+", label: "Safari routes" },
  { value: "4", label: "East African countries" },
  { value: "24/7", label: "Guest support" },
];

const Holidays = () => {
  return (
    <section className="bg-[#f6f1e7] py-20 font-[Poppins]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="relative overflow-hidden rounded-sm bg-[#082f1f] p-7 text-white shadow-2xl shadow-black/15 sm:p-9 lg:min-h-[640px]">
            <img
              src={leopard}
              alt="Leopard safari"
              className="absolute inset-0 h-full w-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#082f1f]/90 via-[#082f1f]/82 to-[#082f1f]" />

            <div className="relative flex h-full flex-col justify-between">
              <div>
                <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731]">
                  Holiday Planning
                </div>

                <h2 className="max-w-xl text-4xl font-extrabold uppercase leading-tight tracking-normal sm:text-5xl">
                  Start Your East African Safari With Confidence
                </h2>

                <p className="mt-5 max-w-xl text-base leading-8 text-white/75">
                  Build a journey around wildlife, primates, culture, beaches,
                  and comfort. We handle the moving parts so your safari feels
                  personal, organized, and unforgettable.
                </p>

                <div className="mt-8 grid gap-3">
                  {tripBenefits.map((benefit) => (
                    <div
                      key={benefit}
                      className="flex items-start gap-3 text-sm leading-6 text-white/80"
                    >
                      <FiCheckCircle className="mt-1 shrink-0 text-lg text-[#F5B731]" />
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative mt-10">
                <div className="grid grid-cols-3 border border-white/15 bg-white/10">
                  {stats.map((stat) => (
                    <div key={stat.label} className="p-4 text-center">
                      <p className="text-2xl font-extrabold text-white">{stat.value}</p>
                      <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-white/60">
                        {stat.label}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/tours"
                    className="inline-flex items-center gap-2 rounded-sm bg-[#F5B731] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f] transition hover:bg-white"
                  >
                    View Tours
                    <FiArrowRight />
                  </Link>
                  <Link
                    to="/contact"
                    className="rounded-sm border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#F5B731] hover:text-[#F5B731]"
                  >
                    Request Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-8 flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div>
                <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
                  Featured Experiences
                </p>
                <h3 className="mt-3 max-w-2xl text-3xl font-extrabold uppercase leading-tight text-[#082f1f] sm:text-4xl">
                  Choose The Holiday Style That Fits Your Journey
                </h3>
              </div>
              <Link
                to="/destinations"
                className="inline-flex w-fit items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f] transition hover:text-[#C88804]"
              >
                See Destinations
                <FiArrowRight />
              </Link>
            </div>

            <div className="grid gap-5 md:grid-cols-3">
              {holidayCards.map((card) => (
                <article
                  key={card.title}
                  className="group overflow-hidden rounded-sm bg-white shadow-xl shadow-black/10"
                >
                  <div className="relative h-60 overflow-hidden">
                    <img
                      src={card.image}
                      alt={card.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <div className="absolute left-4 right-4 top-4 flex items-center justify-between gap-3 text-xs font-bold text-white">
                      <span className="inline-flex items-center gap-1 rounded-sm bg-[#082f1f]/85 px-3 py-2">
                        <FiClock />
                        {card.duration}
                      </span>
                      <span className="rounded-sm bg-[#F5B731] px-3 py-2 text-[#082f1f]">
                        Popular
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-[#F5B731]">
                        <FiMapPin />
                        {card.location}
                      </div>
                      <h4 className="text-xl font-bold">{card.title}</h4>
                    </div>
                  </div>

                  <div className="p-5">
                    <p className="text-sm leading-7 text-[#4f665b]">{card.text}</p>
                    <Link
                      to="/tours"
                      className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#082f1f] transition hover:text-[#C88804]"
                    >
                      Explore package
                      <FiArrowRight />
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="mt-5 grid gap-5 md:grid-cols-3">
              <div className="rounded-sm bg-white p-6 shadow-lg shadow-black/5">
                <div className="mb-4 grid h-12 w-12 place-items-center rounded-sm bg-[#082f1f] text-2xl text-[#F5B731]">
                  <FiCompass />
                </div>
                <h4 className="text-lg font-extrabold text-[#082f1f]">
                  Smart Routing
                </h4>
                <p className="mt-3 text-sm leading-7 text-[#5d7168]">
                  Routes designed around park access, seasons, permits, and
                  comfortable travel time.
                </p>
              </div>

              <div className="rounded-sm bg-white p-6 shadow-lg shadow-black/5">
                <div className="mb-4 grid h-12 w-12 place-items-center rounded-sm bg-[#082f1f] text-2xl text-[#F5B731]">
                  <FiUsers />
                </div>
                <h4 className="text-lg font-extrabold text-[#082f1f]">
                  Private Guidance
                </h4>
                <p className="mt-3 text-sm leading-7 text-[#5d7168]">
                  Travel with experienced guides who know wildlife behavior,
                  local culture, and guest care.
                </p>
              </div>

              <div className="rounded-sm bg-white p-6 shadow-lg shadow-black/5">
                <div className="mb-4 grid h-12 w-12 place-items-center rounded-sm bg-[#082f1f] text-2xl text-[#F5B731]">
                  <FiShield />
                </div>
                <h4 className="text-lg font-extrabold text-[#082f1f]">
                  Reliable Support
                </h4>
                <p className="mt-3 text-sm leading-7 text-[#5d7168]">
                  From inquiry to departure, your details are organized by a
                  responsive travel team.
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-sm bg-[#082f1f] p-6 text-white shadow-lg shadow-black/10">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="flex items-center gap-2 text-[#F5B731]">
                    <FiStar />
                    <span className="text-xs font-bold uppercase tracking-[0.2em]">
                      Recommended
                    </span>
                  </div>
                  <h4 className="mt-3 text-2xl font-extrabold">
                    Combine Wildlife, Primates, Culture, And Coast
                  </h4>
                  <p className="mt-3 max-w-2xl text-sm leading-7 text-white/75">
                    Create one seamless itinerary across Uganda, Kenya, Tanzania,
                    Rwanda, and Zanzibar with every major detail handled.
                  </p>
                </div>

                <Link
                  to="/contact"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-sm bg-white px-5 py-3 text-sm font-bold uppercase tracking-[0.12em] text-[#082f1f] transition hover:bg-[#F5B731]"
                >
                  Plan Trip
                  <FiArrowRight />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Holidays;
